import { useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { useSearchParams } from 'react-router'
import { toast } from 'sonner'
import { BOOKINGS_KEY, CALENDAR_KEY, calendarReturnMessage, type CalendarConnection } from '@/lib/api/queries'

/** The calendar facts every screen shows: the API's `CalendarConnection` (or the bookings list's copy). */
export type CalendarFacts = Pick<
  CalendarConnection,
  'provider' | 'connected' | 'status' | 'email' | 'connected_at' | 'connected_by'
>

export type CalendarState = 'connected' | 'expired' | 'none'

/** Connected (the agent books into it), expired (Google stopped accepting our access), or none. */
export function calendarState(calendar: Pick<CalendarFacts, 'connected' | 'status'> | undefined): CalendarState {
  if (calendar?.status === 'expired') return 'expired'
  return calendar?.connected ? 'connected' : 'none'
}

/** "7 Oct by Kim Alpha", the design's way of saying who connected it and when. */
export function connectedLine(calendar: Pick<CalendarFacts, 'connected_at' | 'connected_by'>, timeZone?: string) {
  if (!calendar.connected_at) return calendar.connected_by ?? '–'
  const day = new Intl.DateTimeFormat('en-AU', { day: 'numeric', month: 'short', timeZone }).format(
    new Date(calendar.connected_at),
  )
  return calendar.connected_by ? `${day} by ${calendar.connected_by}` : day
}

/**
 * Shows what Google's consent screen came back with (`?calendar=…`) once, as a toast,
 * then takes it out of the address so a reload doesn't say it again.
 */
export function useCalendarReturn() {
  const [params, setParams] = useSearchParams()
  const queryClient = useQueryClient()
  const marker = params.get('calendar')
  useEffect(() => {
    if (!marker) return
    toast(calendarReturnMessage(marker).text)
    void queryClient.invalidateQueries({ queryKey: CALENDAR_KEY })
    void queryClient.invalidateQueries({ queryKey: BOOKINGS_KEY })
    setParams(
      (current) => {
        const next = new URLSearchParams(current)
        next.delete('calendar')
        return next
      },
      { replace: true },
    )
  }, [marker, queryClient, setParams])
}
